import React, { useCallback, useEffect } from 'react'
import Field from '@groovehq/internal-design-system/lib/components/Field/Field'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/UnmanagedDrawer'
import { useDispatch, useSelector } from 'react-redux'
import { FormProvider, useForm, useController } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { styles as fieldStyles } from '@groovehq/internal-design-system/lib/components/Field/Field.styles'
import {
  selectIsLoadingCannedReplyCategories,
  selectHasErrorCannedReplyCategories,
  selectIsLoadingCannedReplyVariables,
  selectHasLoadedCannedReplyVariables,
  selectHasErrorCannedReplyVariables,
  selectIsLoadingCannedReplyTemplate,
  selectHasLoadedCannedReplyTemplate,
  selectHasErrorCannedReplyTemplate,
  selectCurrentCannedReplyCategories,
  selectCurrentCannedReplyVariables,
  selectPendingCannedReplyById,
  selectCurrentCannedReplyCategoryById,
} from 'ducks/cannedReplies/selectors'

import {
  doFetchCannedReplyCategoriesV1,
  doFetchCannedReplyVariables,
  doFetchCannedReplyByIdV0Settings,
  doSaveCannedReplyTemplate,
  doSaveCannedReplyTemplateDraft,
  doClearCannedReplyTemplateDraft,
} from 'ducks/cannedReplies/operations'
import { omit } from 'util/objects'
import { buildId } from 'util/globalId'
import { DRAWER_TYPE_CANNED_REPLY_CATEGORIES_UPDATE } from 'ducks/drawers/types'
import { useDrawer } from 'ducks/drawers/hooks'
import MessageTemplateEditor from 'subapps/settings/components/MessageTemplateEditor'
import CategorySelection from '../../CannedReplyCategorySelection'
import { dropdownContainer, topFields, fullWidth } from './styles'

// keys should preferably match the object being stored in the entity store
// that way for updates, you can just spread the entity in reset() and not have to set each property individually
const FORM_SCHEMA = yup.object().shape({
  id: yup.string().nullable(),
  name: yup.string().required('Template name is required'),
  subject: yup.string().required('Email subject is required'),
  body: yup.string().required('Reply body is required'),
  category: yup.string().nullable(), // it's actually the id that is stored here
  // 1. prepend form schema that are not part of the entity with 'form'
  // 2. don't forget to remove it from object being sent onsubmit
  // 3. used to store the selected category dropdown
  formCategory: yup.object().shape({
    id: yup.string().nullable(),
    name: yup.string().nullable(),
  }),
  attachments: yup.array().of(yup.string()),
  formPendingUploads: yup.array(yup.object()),
})

const noCategorySelectOption = { id: null, name: 'Select category' }
const formEmptyState = {
  // defaultValue has to match yup form schema
  id: null,
  name: '',
  subject: '',
  category: null,
  body: '',
  formCategory: noCategorySelectOption,
  attachments: [],
  formPendingUploads: [],
}

export default function CannedReplyCreateEditDrawer({
  drawerResourceId: cannedReplyId,
  drawerSetCategoryId,
  open,
  onClose,
  queryId,
  drawerId,
  ...rest
}) {
  const dispatch = useDispatch()
  const isUpdate = !!cannedReplyId && cannedReplyId !== 'new'

  useEffect(
    () => {
      // need to check drawer open state before fetch dispatches
      // otherwise the dispatch methods in useEffect below will run immediately
      // after this Drawer component is instantiated via `useDrawer` (before openDrawer is even called)
      // Which is unnecessary double dispatch triggering
      if (!open) return

      dispatch(
        doFetchCannedReplyCategoriesV1({
          skipLoaded: false,
        })
      )

      dispatch(doFetchCannedReplyVariables())

      if (isUpdate) {
        // save the response from the API in 'pending' store, not 'current' (because it'll immediately be used in this edit screen)
        dispatch(
          doFetchCannedReplyByIdV0Settings(
            buildId('CannedReply', cannedReplyId),
            { targetStore: 'pending' }
          )
        )
      }
    },
    [dispatch, isUpdate, cannedReplyId, open]
  )

  const categories = useSelector(selectCurrentCannedReplyCategories)

  const variables = useSelector(selectCurrentCannedReplyVariables)

  // not needed for creates but cannot have conditional hooks
  // call anyway, it'll just return null
  const cannedReplyToUpdate = useSelector(state =>
    selectPendingCannedReplyById(state, cannedReplyId)
  )

  // not needed for creates but cannot have conditional hooks
  // call anyway, it'll just return null
  // update: can also be used to load a category created by a previous drawer: drawerSetCategoryId
  const cannedReplyToUpdateCategory = useSelector(state =>
    selectCurrentCannedReplyCategoryById(
      state,
      drawerSetCategoryId || cannedReplyToUpdate?.category
    )
  )

  const isLoadingCategories = useSelector(selectIsLoadingCannedReplyCategories)
  const hasErrorCategories = useSelector(selectHasErrorCannedReplyCategories)
  const isLoadingVariables = useSelector(selectIsLoadingCannedReplyVariables)
  const hasLoadedVariables = useSelector(selectHasLoadedCannedReplyVariables)
  const hasErrorVariables = useSelector(selectHasErrorCannedReplyVariables)
  const isLoadingCannedReplyForUpdate = useSelector(
    selectIsLoadingCannedReplyTemplate
  )
  const hasLoadedCannedReplyForUpdate = useSelector(
    selectHasLoadedCannedReplyTemplate
  )
  const hasErrorCannedReplyForUpdate = useSelector(
    selectHasErrorCannedReplyTemplate
  )
  const isLoadingCannedReply = isUpdate ? isLoadingCannedReplyForUpdate : false
  const hasLoadedCannedReply = isUpdate ? hasLoadedCannedReplyForUpdate : true
  const hasErrorCannedReply = isUpdate ? hasErrorCannedReplyForUpdate : false

  const isLoading =
    isLoadingCategories || isLoadingVariables || isLoadingCannedReply

  const hasError =
    hasErrorCategories || hasErrorVariables || hasErrorCannedReply

  const noCannedReplyFoundById = isUpdate
    ? hasLoadedCannedReply && !cannedReplyToUpdate
    : false

  const canLoadCannedReplyForUpdate = !isLoading && !noCannedReplyFoundById

  const useFormMethods = useForm({
    mode: 'all',
    resolver: yupResolver(FORM_SCHEMA),
    defaultValues: formEmptyState,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState,
    control,
    setValue,
    getValues,
  } = useFormMethods

  const { errors, isValid, isDirty } = formState

  // We're using useController here for controlled components
  // https://react-hook-form.com/v6/api/#Controller
  // https://react-hook-form.com/get-started#IntegratingControlledInputs
  /* const {
    field: { onChange: onBodyChange, onBlur: onBodyBlur, value: bodyValue },
  } = useController({ name: 'body', control }) */

  const {
    field: {
      onChange: onCategoryChange,
      // not necessary considering this is an optional field
      onBlur: onCategoryBlur,
      // what's being returned in value here is one of:
      // 1. defaultValue from useForm
      // 2. category object returned from loading form fields from a template (see reset() further down)
      // 3. similar to above, value returned from a previous drawer e.g. "+ Create New Category"
      // 4. value returned from onCategoryChange (when the dropdown value is changed)
      // All of which need to match the yup form schema FORM_SCHEMA
      value: { id: selectedCategoryId },
      ref: categoryRef,
    },
  } = useController({
    name: 'formCategory',
    control,
  })

  const onCategorySelect = useCallback(
    (_, category) => {
      if (category) {
        // store the 'selected value in dropdown'
        onCategoryChange(category)
        // also set the value of category id in category field (what actually gets saved to entity store)
        setValue('category', category?.id)
      }
    },
    [onCategoryChange, setValue]
  )

  useEffect(
    () => {
      if (!open) return
      if (isUpdate && canLoadCannedReplyForUpdate) {
        // load fields with template values
        reset(
          {
            // just a spread here cos the needed keys should match the form schema
            ...cannedReplyToUpdate,
            category: cannedReplyToUpdateCategory?.id,
            formCategory: cannedReplyToUpdateCategory?.id
              ? {
                  id: cannedReplyToUpdateCategory?.id,
                  name: cannedReplyToUpdateCategory?.name,
                }
              : noCategorySelectOption,
            formPendingUploads: [],
          },
          {
            keepDefaultValues: true,
          }
        )
      }
    },
    // We intentially exclude dependancy props here to ensure tthat this
    // reset only triggers for the initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canLoadCannedReplyForUpdate, isUpdate, open, drawerSetCategoryId]
  )

  useEffect(
    () => {
      if (drawerSetCategoryId && cannedReplyToUpdateCategory) {
        reset(
          {
            ...cannedReplyToUpdate,
            category: cannedReplyToUpdateCategory?.id,
            formCategory: cannedReplyToUpdateCategory?.id
              ? {
                  id: cannedReplyToUpdateCategory?.id,
                  name: cannedReplyToUpdateCategory?.name,
                }
              : noCategorySelectOption,
          },
          {
            keepDefaultValues: true,
          }
        )
      }
    },
    [
      cannedReplyToUpdate,
      drawerSetCategoryId,
      cannedReplyToUpdateCategory,
      reset,
      setValue,
    ]
  )

  const handleOnClose = useCallback(
    () => {
      dispatch(doClearCannedReplyTemplateDraft(cannedReplyId))
      reset(formEmptyState)
      onClose()
    },
    [dispatch, onClose, cannedReplyId, reset]
  )

  const onSubmit = useCallback(
    data => {
      // remove 'form' prefixed fields otherwise it'll get stored in entity store and maybe sent to api
      const sanitizedData = omit(['formCategory', 'formPendingUploads'], data)
      const { formPendingUploads: uploadAttachments } = data

      dispatch(
        doSaveCannedReplyTemplate(cannedReplyId, sanitizedData, {
          uploadAttachments,
          queryId,
        })
      )
      handleOnClose()
    },
    [dispatch, handleOnClose, cannedReplyId, queryId]
  )

  const { openDrawer: openEditDrawer } = useDrawer(
    DRAWER_TYPE_CANNED_REPLY_CATEGORIES_UPDATE
  )

  const handleOnCreateCategory = useCallback(
    () => {
      dispatch(doSaveCannedReplyTemplateDraft(cannedReplyId, getValues()))
      openEditDrawer('new', {
        additionalProps: {
          queryId,
        },
      })
    },
    [dispatch, openEditDrawer, cannedReplyId, getValues, queryId]
  )

  const DrawerForm = useCallback(
    props => <form onSubmit={handleSubmit(onSubmit)} {...props} />,
    [handleSubmit, onSubmit]
  )

  return (
    <FormProvider {...useFormMethods}>
      <Drawer
        {...rest}
        open={open}
        onClose={handleOnClose}
        size="wide"
        title={isUpdate ? 'Edit Canned Reply' : 'New Canned Reply'}
        isLoading={isLoading}
        isError={hasError}
        isNoResultFound={noCannedReplyFoundById}
        footer={
          <ModalBtns
            saveBtnDisabled={isLoading || hasError || !isValid || !isDirty}
            saveBtnText={
              isUpdate ? 'Save Changes' : 'Create a New Canned Reply'
            }
            // type="submit" will automatically trigger the form submit event when clicked
            saveBtnHtmlType="submit"
            tertiaryBtnText="Cancel"
            onClickTertiaryBtn={handleOnClose}
          />
        }
        container={DrawerForm}
      >
        <div className="grui pt-10">
          <div css={topFields} className="grui mb-12">
            <Field
              {...register('name')}
              label="Template Name"
              placeholder="Enter template name..."
              name="name"
              validateStatus={errors?.name ? 'error' : undefined}
              help={errors?.name?.message}
            />
            <div css={dropdownContainer}>
              <div css={fieldStyles.labelBox}>Category</div>
              <CategorySelection
                categories={categories}
                categoryId={selectedCategoryId}
                onSelectCategory={onCategorySelect}
                css={fullWidth}
                showCreate
                onCreateCategory={handleOnCreateCategory}
                buttonRef={categoryRef}
                onButtonBlur={onCategoryBlur}
              />
            </div>
          </div>
          <MessageTemplateEditor
            id={cannedReplyId}
            type="CannedReply"
            entityType="cannedReply"
            open={open}
            mode="managed"
            showPluginImageUpload
            showPluginSourceEditing
            showAddAttachment
            showPluginVariablesPlaceholder
            pluginVariablesPlaceholderTypes={
              hasLoadedVariables ? variables : undefined
            }
            formInputSubjectLabel="Email Subject"
            formInputSubjectPlaceholder="Enter email subject..."
            formInputSubjectTooltip="Inserting a canned reply into an existing email thread doesn't change the subject line. This subject will only be used when you create a new outgoing email."
            formInputBodyLabel="Body"
            queryId={queryId}
          />
        </div>
      </Drawer>
    </FormProvider>
  )
}
